import { Check, BadgePercent } from "lucide-react";
import { PRICING, FOOTER } from "../../config/landingConfig";
import { Reveal } from "./Reveal";
import { SectionTag } from "./decorations";

export const Pricing = () => (
  <section
    id="pakiety"
    data-testid="pricing-section"
    className="scroll-mt-24 border-y-4 border-ink bg-white"
  >
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28">
      <Reveal>
        <SectionTag number="08" label="Pakiety" color="bg-cyan" />
      </Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 text-2xl font-extrabold uppercase tracking-tight text-ink sm:text-3xl lg:text-4xl">
          Wybierz swój pakiet
        </h2>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-ink/70 sm:text-lg">
          Trzy pakiety narastające - każdy kolejny zawiera wszystko z poprzedniego.
        </p>
      </Reveal>
      <div className="mt-12 grid items-stretch gap-8 lg:grid-cols-3 lg:gap-6">
        {PRICING.packages.map((pkg, i) => (
          <Reveal key={pkg.id} delay={i * 0.12} className="h-full">
            <article
              data-testid={`pricing-card-${pkg.id}`}
              className={`relative flex h-full flex-col rounded-3xl border-2 border-ink p-7 ${
                pkg.featured
                  ? "bg-magenta shadow-hard-lg lg:-translate-y-3"
                  : "bg-white shadow-hard"
              }`}
            >
              {pkg.badge && (
                <span
                  data-testid="pricing-featured-badge"
                  className="font-display absolute -top-4 left-1/2 -translate-x-1/2 -rotate-2 whitespace-nowrap rounded-full border-2 border-ink bg-cyan px-5 py-1.5 text-sm uppercase tracking-wide text-ink shadow-hard-sm"
                >
                  {pkg.badge}
                </span>
              )}

              {pkg.thumbs && (
                <div
                  data-testid={`pricing-thumbs-${pkg.id}`}
                  className={`mb-5 grid items-start gap-3 ${
                    pkg.thumbs.length > 2 ? "grid-cols-2" : "grid-cols-1"
                  }`}
                >
                  {pkg.thumbs.map((thumb) => (
                    <div
                      key={thumb.img}
                      className="overflow-hidden rounded-2xl border-2 border-ink bg-white shadow-hard-sm"
                    >
                      <img
                        src={thumb.img}
                        alt={thumb.alt}
                        loading="lazy"
                        className="block h-auto w-full"
                      />
                    </div>
                  ))}
                </div>
              )}

              <h3 className={`font-display mt-2 text-2xl uppercase tracking-wide ${pkg.featured ? "text-white" : "text-ink"}`}>
                {pkg.name}
              </h3>
              <p className={`mt-2 text-sm font-semibold leading-relaxed ${pkg.featured ? "text-white/85" : "text-ink/65"}`}>
                {pkg.desc}
              </p>

              <p className={`mt-6 text-xs font-extrabold uppercase tracking-[0.14em] ${pkg.featured ? "text-white/70" : "text-ink/50"}`}>
                {pkg.intro}
              </p>
              <ul className="mt-3 space-y-2.5">
                {pkg.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2.5">
                    <span
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-ink ${
                        pkg.featured ? "bg-white" : "bg-cyan"
                      }`}
                    >
                      <Check
                        className={`h-3 w-3 ${pkg.featured ? "text-magenta" : "text-ink"}`}
                        strokeWidth={4}
                        aria-hidden="true"
                      />
                    </span>
                    <span className={`text-sm font-semibold leading-snug ${pkg.featured ? "text-white" : "text-ink"}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-auto pt-8">
                <div className="rounded-2xl border-2 border-ink bg-white p-4 shadow-hard-sm">
                  <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-ink/50">
                    Cena pakietu
                  </p>
                  <p data-testid={`pricing-price-${pkg.id}`} className="font-display mt-1 text-3xl text-ink">
                    {pkg.price}
                  </p>
                  {pkg.priceNote && (
                    <span
                      data-testid={`pricing-price-note-${pkg.id}`}
                      className="mt-1.5 inline-block rounded-full border-2 border-ink bg-magenta px-2.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wide text-white"
                    >
                      {pkg.priceNote}
                    </span>
                  )}
                  {pkg.presale && (
                    <p className="mt-1 text-xs font-bold text-ink/55">
                      Przedsprzedaż: <span className="line-through">{pkg.presale}</span>
                    </p>
                  )}
                  {pkg.regular && (
                    <p className="mt-1 text-xs font-bold text-ink/55">
                      {pkg.regularLabel}: <span className="line-through">{pkg.regular}</span>
                    </p>
                  )}
                  {pkg.target && (
                    <p className="mt-1 text-xs font-bold text-ink/55">
                      Cena regularna: <span className="line-through">{pkg.target}</span>
                    </p>
                  )}
                  {pkg.laterNote && (
                    <p
                      data-testid={`pricing-later-note-${pkg.id}`}
                      className="mt-1 text-xs font-extrabold uppercase tracking-wide text-magenta"
                    >
                      {pkg.laterNote}
                    </p>
                  )}
                  {pkg.savings && (
                    <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border-2 border-ink bg-cyan px-3 py-1 text-xs font-extrabold text-ink">
                      <BadgePercent className="h-3.5 w-3.5" aria-hidden="true" />
                      Oszczędzasz: {pkg.savings}
                    </p>
                  )}
                  {PRICING.installmentsNote && (
                    <p className="mt-2 text-xs font-semibold text-ink/60">{PRICING.installmentsNote}</p>
                  )}
                </div>
                <a
                  href={pkg.link}
                  data-testid={`pricing-cta-${pkg.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    window.fbq?.("track", "InitiateCheckout", {
                      content_name: pkg.name,
                      value: parseFloat(pkg.price),
                      currency: "PLN",
                    })
                  }
                  className={`hover-lift mt-4 block rounded-full border-2 border-ink px-6 py-3.5 text-center text-base font-extrabold shadow-hard ${
                    pkg.featured ? "bg-cyan text-ink" : "bg-magenta text-white"
                  }`}
                >
                  {pkg.cta}
                </a>
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <p
          data-testid="pricing-footnote"
          className="mx-auto mt-10 max-w-2xl text-center text-sm font-bold leading-relaxed text-ink/70 sm:text-base"
        >
          {PRICING.footnote}
        </p>
        {PRICING.invoiceNote && (
          <div className="mt-5 text-center">
            <p
              data-testid="pricing-invoice-note"
              className="inline-block rounded-full border-2 border-ink bg-cyan px-5 py-2 text-xs font-extrabold uppercase tracking-wide text-ink shadow-hard-sm sm:text-sm"
            >
              {PRICING.invoiceNote}{" "}
              <a
                data-testid="pricing-invoice-note-link"
                href={`mailto:${FOOTER.email}`}
                className="underline underline-offset-2"
              >
                {FOOTER.email}
              </a>
            </p>
          </div>
        )}
      </Reveal>
    </div>
  </section>
);
